import { useCallback, useEffect, useMemo, useState } from 'react';

export type PaginationMode = 'paginated' | 'loadMore';

export interface PaginationOptions {
  /**
   * `'paginated'` (default) shows one page at a time and exposes
   * `goToPage`. `'loadMore'` accumulates pages and exposes
   * `loadMore`, matching infinite-scroll authoring (SAD §10).
   */
  readonly mode?: PaginationMode;
}

export interface PaginationState<T> {
  readonly visibleItems: ReadonlyArray<T>;
  readonly page: number;
  readonly totalPages: number;
  readonly hasMore: boolean;
  readonly goToPage: (next: number) => void;
  readonly loadMore: () => void;
}

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value));

/**
 * Page or load-more an immutable item list.
 *
 * Disabled (`pageSize` falsy) returns the input list as-is and
 * collapses every control to a no-op so the consumer can render the
 * unpaged collection without a branch (SAD §10).
 *
 * Reset behavior — both modes reset to the first window when the
 * `items` reference changes. Sort and source-status changes are
 * funneled through this reset so authors don't see a stale page-3
 * pointer after a sort flips the order or a re-fetch shrinks the
 * collection (open question Q4 in the plan).
 */
export function usePagination<T>(
  items: ReadonlyArray<T>,
  pageSize: number | undefined,
  options: PaginationOptions = {},
): PaginationState<T> {
  const mode = options.mode ?? 'paginated';
  const enabled = typeof pageSize === 'number' && pageSize > 0;

  const totalPages = useMemo(() => {
    if (!enabled) return items.length === 0 ? 0 : 1;
    if (items.length === 0) return 0;
    return Math.ceil(items.length / (pageSize as number));
  }, [items, pageSize, enabled]);

  const [page, setPage] = useState(0);
  const [loadMoreCount, setLoadMoreCount] = useState(1);

  // Reset window whenever the items reference flips. Source re-fetches
  // and sort flips both produce a new array reference, so this is the
  // single hook that absorbs them. We deliberately compare reference
  // (cheap, deterministic) not deep-equal — re-pagination on identical
  // content is harmless.
  useEffect(() => {
    setPage(0);
    setLoadMoreCount(1);
  }, [items]);

  // If `pageSize` shrinks (e.g. responsive resize) and `page` falls
  // outside the new range, clamp it.
  useEffect(() => {
    if (enabled && page >= totalPages && totalPages > 0) {
      setPage(0);
    }
  }, [enabled, page, totalPages]);

  const goToPage = useCallback(
    (next: number) => {
      if (!enabled) return;
      const last = Math.max(0, totalPages - 1);
      setPage(clamp(next, 0, last));
    },
    [enabled, totalPages],
  );

  const loadMore = useCallback(() => {
    if (!enabled) return;
    setLoadMoreCount((c) => Math.min(c + 1, totalPages));
  }, [enabled, totalPages]);

  return useMemo<PaginationState<T>>(() => {
    if (!enabled) {
      return {
        visibleItems: items,
        page: 0,
        totalPages,
        hasMore: false,
        goToPage,
        loadMore,
      };
    }

    const size = pageSize as number;
    if (mode === 'loadMore') {
      const visible = items.slice(
        0,
        Math.min(items.length, loadMoreCount * size),
      );
      return {
        visibleItems: visible,
        page: 0,
        totalPages,
        hasMore: visible.length < items.length,
        goToPage,
        loadMore,
      };
    }

    const start = page * size;
    const visible = items.slice(start, start + size);
    return {
      visibleItems: visible,
      page,
      totalPages,
      hasMore: page < totalPages - 1,
      goToPage,
      loadMore,
    };
  }, [
    items,
    pageSize,
    enabled,
    mode,
    page,
    loadMoreCount,
    totalPages,
    goToPage,
    loadMore,
  ]);
}
